/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import axios from '../api';
import {
  getTechnicianSkillCache,
  setTechnicianSkillCache,
} from '../utils/indexedDBPersister';
import {
  buildBoard,
  buildTechnicianSkillMap,
} from './calculator';
import CandidatesPanel from './components/CandidatesPanel';
import TechnicianList from './components/TechnicianList';
import './styles.css';

const technicianSkillRequests = new Map();
const simulationResultCache = new Map();
const MAX_SIMULATION_CACHE_ENTRIES = 4;

const simulationCacheKey = (rawRows, calculationOptions, technicianSkillMap, cacheScope, cacheVersion) => {
  const serialized = JSON.stringify(
    { rawRows, calculationOptions, technicianSkillMap, cacheScope, cacheVersion },
    (key, value) => (value instanceof Map ? { __map: [...value.entries()] } : value),
  );
  let hash = 2166136261;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `${serialized.length}:${(hash >>> 0).toString(36)}`;
};

const getSimulationCacheEntry = (key) => {
  if (simulationResultCache.has(key)) {
    const existing = simulationResultCache.get(key);
    simulationResultCache.delete(key);
    simulationResultCache.set(key, existing);
    return existing;
  }
  const entry = { technicians: new Map(), candidates: new Map() };
  simulationResultCache.set(key, entry);
  while (simulationResultCache.size > MAX_SIMULATION_CACHE_ENTRIES) {
    simulationResultCache.delete(simulationResultCache.keys().next().value);
  }
  return entry;
};

const loadTechnicianSkills = async (cacheScope) => {
  const cached = await getTechnicianSkillCache(cacheScope);
  if (cached) return cached;

  const requestKey = String(cacheScope || 'default');
  if (!technicianSkillRequests.has(requestKey)) {
    const request = axios.get('/GetTechnicianSkill')
      .then(async (response) => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        await setTechnicianSkillCache(cacheScope, rows);
        return rows;
      })
      .finally(() => technicianSkillRequests.delete(requestKey));
    technicianSkillRequests.set(requestKey, request);
  }
  return technicianSkillRequests.get(requestKey);
};

function LoadingPanel({ compact = false, title, detail }) {
  return (
    <div className={`simulation-loading${compact ? ' simulation-loading-compact' : ''}`}>
      <div className="simulation-loading-icon" aria-hidden="true"><span /></div>
      <div className="simulation-loading-copy">
        <strong>{title}</strong>
        <span>{detail}</span>
      </div>
      {!compact && (
        <div className="simulation-skeleton" aria-hidden="true">
          {[0, 1, 2].map(item => <span key={item} />)}
        </div>
      )}
    </div>
  );
}

export default function App({ rawRows, calculatedRows, calculationOptions, cacheScope, cacheVersion, onMove, onShowMap }) {
  const [board, setBoard] = useState(null);
  const [bestMoves, setBestMoves] = useState(new Map());
  const [analysisProgress, setAnalysisProgress] = useState({ done: 0, total: 0, active: false });
  const [analysisTechCode, setAnalysisTechCode] = useState(null);
  const [boardError, setBoardError] = useState(null);
  const [selectedOfscNr, setSelectedOfscNr] = useState(null);
  const [candidateData, setCandidateData] = useState(null);
  const [candidateStatus, setCandidateStatus] = useState(null);
  const [technicianSkillMap, setTechnicianSkillMap] = useState(null);
  const [skillLoadError, setSkillLoadError] = useState(false);
  const [backgroundCacheTick, setBackgroundCacheTick] = useState(0);
  const [workerInstance, setWorkerInstance] = useState(null);
  const [workerReady, setWorkerReady] = useState(false);
  const [workerError, setWorkerError] = useState(null);
  const candidateRequestRef = useRef(0);
  const workerRequestSequenceRef = useRef(0);
  const workerContextVersionRef = useRef(0);
  const workerHandlersRef = useRef(new Map());
  const technicianAnalysisCacheRef = useRef(new Map());
  const activeSimulationCacheRef = useRef(null);
  const foregroundTechRef = useRef(null);

  useEffect(() => {
    let worker;
    try {
      worker = new Worker(new URL('./simulation.worker.js', import.meta.url));
      worker.onmessage = (event) => {
        const message = event.data || {};
        if (message.type === 'configured') {
          if (message.contextVersion === workerContextVersionRef.current) {
            setWorkerReady(true);
            setWorkerError(null);
          }
          return;
        }
        const handler = workerHandlersRef.current.get(message.requestId);
        if (!handler) return;
        handler(message);
        if (['analysisComplete', 'candidatesComplete', 'requestError', 'requestCancelled'].includes(message.type)) {
          workerHandlersRef.current.delete(message.requestId);
        }
      };
      worker.onerror = (event) => {
        setWorkerReady(false);
        setWorkerError(event.message || 'Simülasyon Worker başlatılamadı.');
      };
      setWorkerInstance(worker);
    } catch (error) {
      setWorkerError(error.message);
    }
    return () => {
      workerHandlersRef.current.clear();
      worker?.terminate();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setTechnicianSkillMap(null);
    setSkillLoadError(false);
    loadTechnicianSkills(cacheScope)
      .then((rows) => {
        if (!cancelled) setTechnicianSkillMap(buildTechnicianSkillMap(rows));
      })
      .catch(() => {
        if (cancelled) return;
        setSkillLoadError(true);
        setTechnicianSkillMap(new Map());
      });
    return () => { cancelled = true; };
  }, [cacheScope]);

  useEffect(() => {
    if (!workerInstance || technicianSkillMap == null) return;
    const contextVersion = workerContextVersionRef.current + 1;
    workerContextVersionRef.current = contextVersion;
    workerHandlersRef.current.clear();
    const cacheKey = simulationCacheKey(
      rawRows,
      calculationOptions,
      technicianSkillMap,
      cacheScope,
      cacheVersion,
    );
    const cacheEntry = getSimulationCacheEntry(cacheKey);
    activeSimulationCacheRef.current = cacheEntry;
    technicianAnalysisCacheRef.current = new Map(cacheEntry.technicians);
    setWorkerReady(false);
    setWorkerError(null);
    try {
      workerInstance.postMessage({
        type: 'configure',
        contextVersion,
        rawRows,
        calculatedRows,
        calculationOptions,
        technicianSkillMap,
      });
    } catch (error) {
      setWorkerError(error.message);
    }
  }, [workerInstance, rawRows, calculatedRows, calculationOptions, technicianSkillMap, cacheScope, cacheVersion]);

  useEffect(() => {
    let cancelled = false;
    foregroundTechRef.current = null;
    setBoard(null);
    setBoardError(null);
    setBestMoves(new Map());
    setAnalysisTechCode(null);
    setAnalysisProgress({ done: 0, total: 0, active: false });
    const frame = window.requestAnimationFrame(() => {
      try {
        const boardData = buildBoard(calculatedRows, calculationOptions);
        if (!cancelled) setBoard(boardData);
      } catch (error) {
        if (!cancelled) {
          setBoardError(error.message);
          setBoard({ technicians: [] });
        }
      }
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    };
  }, [calculatedRows, calculationOptions, cacheVersion]);

  useEffect(() => {
    if (!analysisTechCode || !board || !workerReady || boardError || workerError) return undefined;
    const requestId = workerRequestSequenceRef.current + 1;
    workerRequestSequenceRef.current = requestId;
    const calculatedBestMoves = new Map();
    setAnalysisProgress({ done: 0, total: 0, active: true });
    workerHandlersRef.current.set(requestId, (message) => {
      if (message.type === 'analysisStarted') {
        setAnalysisProgress({ done: 0, total: message.total, active: message.total > 0 });
      } else if (message.type === 'analysisProgress') {
        if (message.bestMove) {
          calculatedBestMoves.set(message.bestMove.ofscNr, message.bestMove);
          setBestMoves((current) => {
            const next = new Map(current);
            next.set(message.bestMove.ofscNr, message.bestMove);
            return next;
          });
        }
        setAnalysisProgress({ done: message.done, total: message.total, active: message.done < message.total });
      } else if (message.type === 'analysisComplete') {
        technicianAnalysisCacheRef.current.set(
          String(analysisTechCode).trim(),
          new Map(calculatedBestMoves),
        );
        activeSimulationCacheRef.current?.technicians.set(
          String(analysisTechCode).trim(),
          new Map(calculatedBestMoves),
        );
        if (foregroundTechRef.current === String(analysisTechCode).trim()) {
          foregroundTechRef.current = null;
        }
        setAnalysisProgress({ done: message.total, total: message.total, active: false });
        setAnalysisTechCode(null);
      } else if (message.type === 'requestError') {
        setWorkerError(message.message);
        setAnalysisProgress({ done: 0, total: 0, active: false });
        setAnalysisTechCode(null);
      }
    });
    workerInstance.postMessage({
      type: 'analyzeTechnician',
      requestId,
      contextVersion: workerContextVersionRef.current,
      techCode: analysisTechCode,
    });
    return () => {
      workerHandlersRef.current.delete(requestId);
      workerInstance.postMessage({ type: 'cancel', requestId });
    };
  }, [analysisTechCode, board, boardError, workerError, workerInstance, workerReady]);

  useEffect(() => {
    if (
      !board
      || !workerReady
      || boardError
      || workerError
      || analysisTechCode
      || analysisProgress.active
    ) return undefined;

    const nextTechnician = board.technicians.find((technician) => (
      !technicianAnalysisCacheRef.current.has(String(technician.techCode).trim())
    ));
    if (!nextTechnician) return undefined;

    const techCode = String(nextTechnician.techCode).trim();
    const requestId = workerRequestSequenceRef.current + 1;
    workerRequestSequenceRef.current = requestId;
    const calculatedBestMoves = new Map();
    workerHandlersRef.current.set(requestId, (message) => {
      if (message.type === 'analysisProgress' && message.bestMove) {
        calculatedBestMoves.set(message.bestMove.ofscNr, message.bestMove);
      } else if (message.type === 'analysisComplete') {
        technicianAnalysisCacheRef.current.set(techCode, new Map(calculatedBestMoves));
        activeSimulationCacheRef.current?.technicians.set(techCode, new Map(calculatedBestMoves));
        setBackgroundCacheTick(current => current + 1);
      } else if (message.type === 'requestError') {
        setWorkerError(message.message);
      }
    });
    workerInstance.postMessage({
      type: 'analyzeTechnician',
      requestId,
      contextVersion: workerContextVersionRef.current,
      techCode: nextTechnician.techCode,
    });
    return () => {
      workerHandlersRef.current.delete(requestId);
      workerInstance.postMessage({ type: 'cancel', requestId });
    };
  }, [
    analysisProgress.active,
    analysisTechCode,
    backgroundCacheTick,
    board,
    boardError,
    cacheVersion,
    workerError,
    workerInstance,
    workerReady,
  ]);

  useEffect(() => {
    if (selectedOfscNr == null || !workerReady || workerError) return undefined;
    const cachedCandidate = activeSimulationCacheRef.current?.candidates.get(String(selectedOfscNr));
    if (cachedCandidate) {
      setCandidateData(cachedCandidate);
      setCandidateStatus(null);
      return undefined;
    }
    const requestId = workerRequestSequenceRef.current + 1;
    workerRequestSequenceRef.current = requestId;
    candidateRequestRef.current = requestId;
    setCandidateStatus('loading');
    workerHandlersRef.current.set(requestId, (message) => {
      if (candidateRequestRef.current !== requestId) return;
      if (message.type === 'candidatesComplete') {
        if (message.result == null) {
          setCandidateStatus('İş bulunamadı.');
          return;
        }
        activeSimulationCacheRef.current?.candidates.set(String(selectedOfscNr), message.result);
        setCandidateData(message.result);
        setCandidateStatus(null);
      } else if (message.type === 'requestError') {
        setCandidateStatus(`Hata: ${message.message}`);
      }
    });
    workerInstance.postMessage({
      type: 'getCandidates',
      requestId,
      contextVersion: workerContextVersionRef.current,
      ofscNr: selectedOfscNr,
    });
    return () => {
      workerHandlersRef.current.delete(requestId);
      workerInstance.postMessage({ type: 'cancel', requestId });
    };
  }, [selectedOfscNr, workerError, workerInstance, workerReady]);

  function handleSelectJob(ofscNr) {
    setSelectedOfscNr(ofscNr);
    setCandidateData(null);
    setCandidateStatus('loading');
  }

  function handleTechnicianToggle(techCode, willOpen) {
    candidateRequestRef.current = 0;
    setSelectedOfscNr(null);
    setCandidateData(null);
    setCandidateStatus(null);
    const cacheKey = String(techCode).trim();
    const cachedBestMoves = willOpen
      ? technicianAnalysisCacheRef.current.get(cacheKey)
      : null;
    foregroundTechRef.current = willOpen && !cachedBestMoves ? cacheKey : null;
    setBestMoves(cachedBestMoves ? new Map(cachedBestMoves) : new Map());
    setAnalysisTechCode(willOpen && !cachedBestMoves ? techCode : null);
    setAnalysisProgress({ done: 0, total: 0, active: willOpen && !cachedBestMoves });
  }

  function handleCandidateMap(payload) {
    if (!onShowMap) return;
    const sourceTech = board?.technicians.find(
      tech => String(tech.techCode || '').trim() === String(candidateData?.donorTechCode || '').trim(),
    );
    onShowMap({
      ...payload,
      comparison: true,
      sourceTechCode: candidateData?.donorTechCode,
      sourceTechName: candidateData?.donorTechName,
      sourceRoute: sourceTech?.jobs || [],
      impact: {
        kmDelta: Number(payload.kmDelta) || 0,
      },
    });
  }

  const statusText = (() => {
    if (boardError) return `Pano hazırlanamadı: ${boardError}`;
    if (workerError) return `Simülasyon başlatılamadı: ${workerError}`;
    if (!board) return 'Planlama verileri hazırlanıyor';
    if (technicianSkillMap == null) return `${board.technicians.length} teknisyen hazır · Uzmanlık bilgileri alınıyor`;
    if (!workerReady) return `${board.technicians.length} teknisyen hazır · Simülasyon motoru hazırlanıyor`;
    if (analysisProgress.active) {
      return `${analysisProgress.done}/${analysisProgress.total} iş karşılaştırıldı · Sonuçlar tabloya ekleniyor`;
    }
    if (skillLoadError) return `${board.technicians.length} teknisyen hazır · Uzmanlık bilgileri alınamadı`;
    if (bestMoves.size > 0) return `${board.technicians.length} teknisyen hazır · Seçili teknisyen analizi tamamlandı`;
    return `${board.technicians.length} teknisyen hazır · Analiz için bir teknisyen açın`;
  })();
  const progressPercent = analysisProgress.total
    ? Math.round((analysisProgress.done / analysisProgress.total) * 100)
    : 0;

  return (
    <div className="move-simulation">
      <div className="layout">
            <div className="left-col">
              {board == null ? (
                <LoadingPanel title="Planlama panosu hazırlanıyor" detail="Teknisyen rotaları düzenleniyor…" />
              ) : (
                <TechnicianList
                  technicians={board.technicians}
                  bestMoves={bestMoves}
                  selectedOfscNr={selectedOfscNr}
                  onSelectJob={handleSelectJob}
                  onToggleTechnician={handleTechnicianToggle}
                />
              )}
            </div>
            <div className="right-col">
              {analysisProgress.active ? (
                <div className="analysis-screen" role="status" aria-live="polite">
                  <div className="analysis-screen-card">
                    <div className="analysis-screen-spinner" aria-hidden="true"><span /></div>
                    <span className="analysis-active-badge">
                      <i aria-hidden="true" />
                      ANALİZ SÜRÜYOR
                    </span>
                    <strong>Taşıma seçenekleri analiz ediliyor</strong>
                    <p>{statusText}</p>
                    <div className="analysis-screen-progress-label">
                      <span>Tamamlanma</span>
                      <b>%{progressPercent}</b>
                    </div>
                    <div className="analysis-progress" aria-label={`Taşıma analizi yüzde ${progressPercent}`}>
                      <span style={{ width: `${progressPercent}%` }} />
                    </div>
                    <small>Analiz tamamlandığında işlerin en iyi taşıma sonuçları listeye eklenecektir.</small>
                  </div>
                </div>
              ) : candidateStatus === 'loading' ? (
                <LoadingPanel
                  compact
                  title="Aday teknisyenler değerlendiriliyor"
                  detail="Rota ve uzmanlık uygunlukları karşılaştırılıyor…"
                />
              ) : candidateStatus ? (
                <div className="empty">{candidateStatus}</div>
              ) : candidateData ? (
                <CandidatesPanel data={candidateData} onMove={onMove} onShowMap={handleCandidateMap} />
              ) : (
                <div className="empty empty-guidance">
                  <span className="empty-guidance-icon">↔</span>
                  <strong>Taşınacak işi seçin</strong>
                  <span>Aday teknisyenleri görmek için soldaki listeden bir işe tıklayın.</span>
                </div>
              )}
            </div>
      </div>
      {!analysisProgress.active && (
          <div id="status">
            <div className="status-line">
              <span className="status-dot" />
              <span>{statusText}</span>
            </div>
          </div>
      )}
    </div>
  );
}
